import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Truck,
  Plus,
  MagnifyingGlass,
  Package,
  Clock,
  ChartBar,
  ArrowsClockwise,
  DownloadSimple,
  Users,
  ClockCounterClockwise,
} from "@phosphor-icons/react";
import { api, STATUS_OPTIONS } from "@/lib/lorryApi";
import { exportLorriesToCsv } from "@/lib/csvExport";
import HistorySheet, { NextResetPill, useNextReset } from "@/components/HistorySheet";
import StatCard from "@/components/StatCard";
import LorryTable from "@/components/LorryTable";
import LorryFormDialog from "@/components/LorryFormDialog";
import DriversSheet from "@/components/DriversSheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ALL = "__all__";
const AUTO_REFRESH_MS = 30000;

export default function Dashboard() {
  const [lorries, setLorries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoSyncing, setAutoSyncing] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [driversOpen, setDriversOpen] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const dialogOpenRef = useRef(false);
  const nextReset = useNextReset();

  useEffect(() => {
    dialogOpenRef.current = dialogOpen || !!deleteTarget;
  }, [dialogOpen, deleteTarget]);

  const fetchLorries = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);
      try {
        const params = {};
        if (search.trim()) params.search = search.trim();
        if (statusFilter !== ALL) params.status = statusFilter;
        const res = await api.get("/lorries", { params });
        setLorries(res.data);
        setLastUpdated(new Date());
      } catch (e) {
        if (!silent) toast.error("Failed to load entries");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [search, statusFilter]
  );

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get("/lorries/stats");
      setStats(res.data);
    } catch (e) {
      // silent
    }
  }, []);

  const fetchDrivers = useCallback(async () => {
    try {
      const res = await api.get("/drivers");
      setDrivers(res.data);
    } catch (e) {
      // silent
    }
  }, []);

  const fetchRegistrations = useCallback(async () => {
    try {
      const res = await api.get("/registrations");
      setRegistrations(res.data);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchDrivers();
    fetchRegistrations();
  }, [fetchDrivers, fetchRegistrations]);

  useEffect(() => {
    const t = setTimeout(fetchLorries, 250);
    return () => clearTimeout(t);
  }, [fetchLorries]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats, lorries.length]);

  // Auto-refresh every 30s (pause while a dialog is open or the tab is hidden)
  useEffect(() => {
    const tick = async () => {
      if (dialogOpenRef.current) return;
      if (document.visibilityState !== "visible") return;
      setAutoSyncing(true);
      try {
        await Promise.all([
          fetchLorries({ silent: true }),
          fetchStats(),
          fetchDrivers(),
          fetchRegistrations(),
        ]);
      } finally {
        setTimeout(() => setAutoSyncing(false), 400);
      }
    };
    const id = setInterval(tick, AUTO_REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [fetchLorries, fetchStats, fetchDrivers, fetchRegistrations]);

  const refresh = () => {
    fetchLorries();
    fetchStats();
    fetchDrivers();
    fetchRegistrations();
  };

  const handleExport = () => {
    if (!lorries.length) {
      toast.error("Nothing to export yet");
      return;
    }
    const count = exportLorriesToCsv(lorries);
    toast.success(`Exported ${count} ${count === 1 ? "entry" : "entries"} to CSV`);
  };

  const handleEdit = (lorry) => {
    setEditing(lorry);
    setDialogOpen(true);
  };

  const handleAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/lorries/${deleteTarget.id}`);
      toast.success(`Entry ${deleteTarget.registration} removed`);
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const topProduct = useMemo(() => {
    if (!stats?.top_products?.length) return "—";
    return stats.top_products[0].product;
  }, [stats]);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 relative z-[2]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-zinc-950/95 backdrop-blur border-b border-zinc-800">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-3 sm:py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
            <div className="w-8 h-8 sm:w-9 sm:h-9 bg-yellow-500 flex items-center justify-center flex-shrink-0">
              <Truck size={20} weight="fill" className="text-zinc-950" />
            </div>
            <div className="min-w-0">
              <div className="font-display font-black text-base sm:text-lg leading-none tracking-tight">
                LOAD<span className="text-yellow-500">/</span>YARD
              </div>
              <div
                data-testid="sync-status"
                className="mt-1 inline-flex items-center gap-1.5"
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${
                    autoSyncing ? "bg-yellow-500 animate-pulse" : "bg-emerald-500"
                  }`}
                />
                <span className="overline text-zinc-500 text-[0.6rem] sm:text-xs">
                  {autoSyncing
                    ? "Syncing…"
                    : lastUpdated
                    ? `Live · ${lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                    : "Live"}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NextResetPill info={nextReset} />
            <Button
              data-testid="history-btn"
              onClick={() => setHistoryOpen(true)}
              variant="outline"
              className="rounded-sm bg-zinc-900 border-zinc-800 text-zinc-300 hover:text-yellow-500 hover:border-yellow-500/40 hover:bg-zinc-900 px-3 sm:px-4"
              title="Reset history"
            >
              <ClockCounterClockwise size={18} weight="bold" className="sm:mr-1.5" />
              <span className="hidden sm:inline text-xs tracking-widest uppercase font-semibold">
                History
              </span>
            </Button>
            <Button
              data-testid="drivers-btn"
              onClick={() => setDriversOpen(true)}
              variant="outline"
              className="rounded-sm bg-zinc-900 border-zinc-800 text-zinc-300 hover:text-yellow-500 hover:border-yellow-500/40 hover:bg-zinc-900 px-3 sm:px-4"
            >
              <Users size={18} weight="bold" className="sm:mr-1.5" />
              <span className="hidden sm:inline text-xs tracking-widest uppercase font-semibold">
                Drivers <span className="text-zinc-500 ml-1">{drivers.length}</span>
              </span>
            </Button>
            <Button
              data-testid="new-entry-btn"
              onClick={handleAdd}
              className="rounded-sm bg-yellow-500 hover:bg-yellow-400 text-zinc-950 font-semibold tracking-wide active:scale-[0.98] transition-transform px-3 sm:px-4"
            >
              <Plus size={18} weight="bold" className="sm:mr-1.5" />
              <span className="hidden sm:inline">New Entry</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-8 lg:py-12">
        {/* Hero */}
        <section className="mb-8 sm:mb-10">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-6 sm:w-8 h-[2px] bg-yellow-500" />
            <span className="overline text-yellow-500">Operations Console</span>
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter">
            The Yard,
            <br />
            <span className="text-zinc-500">In Real Time.</span>
          </h1>
          <p className="text-zinc-400 mt-4 sm:mt-5 max-w-xl text-sm sm:text-base leading-relaxed">
            Track every lorry, load and destination. Log registrations, driver
            assignments and cargo status without leaving this screen.
          </p>
        </section>

        {/* Stats Grid */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-10">
          <StatCard
            testId="stat-total-loads"
            label="Total Loads"
            value={stats?.total_load_tons ?? 0}
            unit="Tons"
            iconName="chart"
            accent="yellow"
          />
          <StatCard
            testId="stat-active"
            label="Active Lorries"
            value={stats?.active_lorries ?? 0}
            unit="On Road"
            iconName="truck"
            accent="blue"
          />
          <StatCard
            testId="stat-today"
            label="Loads Today"
            value={stats?.loads_today ?? 0}
            unit="Entries"
            iconName="clock"
            accent="orange"
          />
          <StatCard
            testId="stat-top-product"
            label="Top Product"
            value={topProduct}
            unit=""
            iconName="package"
            accent="emerald"
          />
        </section>

        {/* Status breakdown strip */}
        {stats && (
          <section
            data-testid="status-strip"
            className="mb-6 sm:mb-10 border border-zinc-800 bg-zinc-900/40 divide-y sm:divide-y-0 sm:divide-x divide-zinc-800 grid grid-cols-2 sm:grid-cols-4"
          >
            {STATUS_OPTIONS.map((s) => (
              <div key={s.value} className="p-4 flex items-center justify-between">
                <div>
                  <div className="overline text-zinc-500">{s.label}</div>
                  <div className="font-display font-bold text-2xl mt-1 text-zinc-100">
                    {stats.by_status?.[s.value] ?? 0}
                  </div>
                </div>
                <div className={`w-2 h-10 ${dotBar(s.value)}`} />
              </div>
            ))}
          </section>
        )}

        {/* Toolbar */}
        <section className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <MagnifyingGlass
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
            />
            <Input
              data-testid="search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search registration, product or driver…"
              className="pl-9 bg-zinc-900 border-zinc-800 rounded-sm focus-visible:ring-yellow-500 h-11"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger
              data-testid="filter-status"
              className="w-full sm:w-56 bg-zinc-900 border-zinc-800 rounded-sm h-11 focus:ring-yellow-500"
            >
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 rounded-sm">
              <SelectItem value={ALL}>All statuses</SelectItem>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            data-testid="refresh-btn"
            onClick={refresh}
            variant="outline"
            className="rounded-sm bg-zinc-900 border-zinc-800 text-zinc-300 hover:text-yellow-500 hover:border-yellow-500/40 hover:bg-zinc-900 h-11"
          >
            <ArrowsClockwise size={16} weight="bold" />
          </Button>
          <Button
            data-testid="export-csv-btn"
            onClick={handleExport}
            variant="outline"
            className="rounded-sm bg-zinc-900 border-zinc-800 text-zinc-300 hover:text-yellow-500 hover:border-yellow-500/40 hover:bg-zinc-900 h-11"
          >
            <DownloadSimple size={16} weight="bold" className="sm:mr-1.5" />
            <span className="hidden sm:inline text-xs tracking-widest uppercase font-semibold">Export CSV</span>
          </Button>
        </section>

        {/* Table */}
        <LorryTable
          lorries={lorries}
          loading={loading}
          onEdit={handleEdit}
          onDelete={(l) => setDeleteTarget(l)}
        />

        <footer className="mt-16 border-t border-zinc-900 pt-6 text-xs text-zinc-600 flex flex-col sm:flex-row gap-2 sm:justify-between">
          <span className="overline">LOAD/YARD · v1</span>
          <div className="flex items-center gap-2">
            <span className="overline text-zinc-500">
              Auto-refresh every 30s
              {lastUpdated
                ? ` · last ${lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}`
                : ""}
            </span>
            <span className="text-zinc-700">·</span>
            <span>{lorries.length} shown</span>
          </div>
        </footer>
      </main>

      <LorryFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        onSaved={refresh}
        drivers={drivers}
        registrations={registrations}
      />

      <DriversSheet
        open={driversOpen}
        onOpenChange={setDriversOpen}
        drivers={drivers}
        onChanged={fetchDrivers}
      />

      <HistorySheet
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        onResetDone={refresh}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent
          data-testid="delete-dialog"
          className="bg-zinc-950 border border-zinc-800 rounded-none"
        >
          <AlertDialogHeader>
            <span className="overline text-red-500">Danger</span>
            <AlertDialogTitle className="font-display text-2xl font-black">
              Remove lorry entry?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will permanently delete{" "}
              <span className="text-zinc-100 font-mono">
                {deleteTarget?.registration}
              </span>{" "}
              from the yard log. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="delete-cancel"
              className="rounded-sm bg-zinc-900 border-zinc-800 text-zinc-300 hover:bg-zinc-800"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm"
              onClick={confirmDelete}
              className="rounded-sm bg-red-600 hover:bg-red-500 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function dotBar(status) {
  switch (status) {
    case "loaded":
      return "bg-blue-500";
    case "dispatched":
      return "bg-orange-500";
    case "delivered":
      return "bg-emerald-500";
    default:
      return "bg-yellow-500";
  }
}
